import useSession from '@/utils/hooks/useSession';
import moment from 'moment-timezone';

export function getUTCEpochTime() {
  return moment().valueOf();
}

// Get the current time as either a string or moment.js object.
export function getCurrentTime(timezone = 'GMT', raw = false) {
  const time = moment().tz(timezone);
  return raw ? time : time.format();
}

export function getTimezones() {
  // Moment has well formatted data for timezone name and utc offset, but not the long
  // name. We use the Intl API to get the long name of the timezone.
  const intlTimezoneNames = Intl.supportedValuesOf('timeZone');
  const momentTimezoneNames = moment.tz.names();
  const supportedTimezoneNames = momentTimezoneNames.filter((name) =>
    intlTimezoneNames.includes(name)
  );

  const timeZoneStringMap = {};
  for (const timezoneName of supportedTimezoneNames) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezoneName,
      timeZoneName: 'long',
    });
    const result = formatter.formatToParts(new Date());
    const longName = result.find((part) => part.type === 'timeZoneName').value;

    const utcOffset = moment.tz(timezoneName).utcOffset() / 60;
    let utcOffsetString = utcOffset.toString(); // 1, 10, -1, -10
    if (!utcOffsetString.startsWith('-')) {
      utcOffsetString = `+${utcOffsetString}`;
    } // +1, +10, -1, -10
    if (utcOffsetString.length === 2) {
      utcOffsetString = utcOffsetString[0] + '0' + utcOffsetString[1];
    } // +01, +10, -01, -10
    utcOffsetString = `UTC${utcOffsetString}:00`; // UTC+01:00, UTC+10:00, UTC-01:00, UTC-10:00

    const formattedString = `${timezoneName} (${utcOffsetString}) - ${longName}`;

    timeZoneStringMap[timezoneName] = formattedString;
  }

  return timeZoneStringMap;
}

export function getStandardFormat(format, timezone = false) {
  const formats = {
    iso: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
    date: 'DD MMM YYYY',
    isoDate: 'YYYY-MM-DD',
    isoDateTimeWithSeconds: 'YYYY-MM-DD HH:mm:ss',
    time: 'HH:mm',
    dateTime: 'DD MMM YYYY HH:mm',
    dateTimeWithSeconds: 'DD MMM YYYY HH:mm:ss',
    dateTimeWithMilliSeconds: 'DD MMM YYYY HH:mm:ss.SSS',
  };
  const selectedFormat = formats[format];

  if (!selectedFormat)
    throw new Error(`Invalid format passed to datetime module: ${format}`);

  if (timezone) {
    return `${selectedFormat} z`;
  }
  return selectedFormat;
}

// Hook to allow components to access the renderTime function.
export function useRenderTime() {
  const { user } = useSession();
  return {
    renderTime: (timeNoOffset, format, args) =>
      renderTime(timeNoOffset, format, user, args),
  };
}

// Render time in the given format.
export function renderTime(timeNoOffset, format, user, args = {}) {
  const { nullValue, timezoneOverride, displayTimezone } = args;

  if (
    timeNoOffset === null ||
    timeNoOffset === undefined ||
    timeNoOffset === ''
  ) {
    return nullValue || 'NULL';
  }

  // Accepts standard formats or custom formats.
  let formatToUse;
  try {
    formatToUse = getStandardFormat(format, displayTimezone);
  } catch {
    formatToUse = format;
  }
  // Defaults to users Preferred Timezone, which defaults to 'America/New_York'.
  const timezone = timezoneOverride || user?.timezone || 'America/New_York';
  // TimeNoOffset must be a zero-offset time string (ends with 'Z') or a UTC time string.

  const time = moment(timeNoOffset, true).tz(timezone);
  if (!time.isValid()) return 'Invalid Date Format';
  return time.format(formatToUse);
}

// Given 2 date strings or Date objects, return the difference in milliseconds.
export function compareTimes(a, b) {
  return moment(b) - moment(a);
}

export function convertBadSqlTimestamp(timestamp, actualTimezone) {
  // When the SQL utility gets a timestamp with no offset, I.E '2023-05-05 14:09:02.187',
  // it will assume the timestamp is GMT and re-write it to '2023-05-05 14:09:02.187Z'.
  // This function allows you to unwind that into whatever timezone the timestamp was
  // supposed to be in.

  const time = moment(timestamp);

  const minutesOffset = moment.tz(actualTimezone).utcOffset();
  time.add(-minutesOffset, 'minutes');

  // Grabbing via zone will always return the offset for current date, to calculate
  // daylight savings time, we check the current DST bool and the DST bool for the
  // timestamp we are acting on.
  const isDst = moment().isDST();
  const isDstTimestamp = moment(timestamp).isDST();
  const dstOffset = isDst - isDstTimestamp;
  time.add(dstOffset, 'hours');

  return time.format();
}
